// Time zones: every time the dashboard and the affiliate portal show is in the zone the person chose (UTC until they choose). Times are kept as
// universal timestamps (milliseconds since 1970); only what is shown, and where a day begins and ends, depends on the zone.
export const DEFAULT_ZONE = 'UTC';

/** Whether this is a time zone name the browser or server knows, like Asia/Jerusalem or UTC. */
export function validZone(zone) {
  if (typeof zone !== 'string' || !zone || zone.length > 64) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** The zone to use: the given one when it is valid, else UTC. */
export const zoneOr = (zone) => (validZone(zone) ? zone : DEFAULT_ZONE);

/** Every zone name this browser knows (UTC first). */
export function allZones() {
  let list = [];
  try {
    list = Intl.supportedValuesOf('timeZone');
  } catch {
    list = ['Africa/Cairo', 'America/Chicago', 'America/Los_Angeles', 'America/New_York', 'America/Sao_Paulo', 'Asia/Dubai', 'Asia/Jerusalem', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney', 'Europe/Berlin', 'Europe/Kyiv', 'Europe/London', 'Europe/Moscow', 'Europe/Paris'];
  }
  return ['UTC', ...list.filter((z) => z !== 'UTC')];
}

const FORMATS = new Map();
const partsFormat = (zone) => {
  if (!FORMATS.has(zone)) FORMATS.set(zone, new Intl.DateTimeFormat('en-US', { timeZone: zone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', weekday: 'short' }));
  return FORMATS.get(zone);
};
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** The calendar and clock fields of a moment in a zone: { year, month (1-12), day, hour, minute, second, weekday (0 = Sunday) }. */
export function zonedParts(ms, zone = DEFAULT_ZONE) {
  const p = Object.fromEntries(partsFormat(zoneOr(zone)).formatToParts(new Date(ms)).map((x) => [x.type, x.value]));
  return { year: Number(p.year), month: Number(p.month), day: Number(p.day), hour: Number(p.hour) % 24, minute: Number(p.minute), second: Number(p.second), weekday: WEEKDAYS.indexOf(p.weekday) };
}

/** How far the zone's clock is ahead of UTC at that moment, in milliseconds (Jerusalem in summer: +3 hours). */
export function offsetMs(ms, zone = DEFAULT_ZONE) {
  const p = zonedParts(ms, zone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(ms / 1000) * 1000;
}

/** The moment at which a zone's clock reads this date and time (through daylight-saving changes). */
export function zonedTime(year, month, day, hour = 0, minute = 0, second = 0, zone = DEFAULT_ZONE) {
  const naive = Date.UTC(year, month - 1, day, hour, minute, second);
  let guess = naive - offsetMs(naive, zone);
  guess = naive - offsetMs(guess, zone); // the offset at the answer, which can differ from the one at the first guess
  return guess;
}

const DAY_TEXT = /^(\d{4})-(\d{2})-(\d{2})$/;
/** "2026-10-07" -> the start of that day in the zone, or NaN. */
export function dayStartOf(text, zone = DEFAULT_ZONE) {
  const m = DAY_TEXT.exec(text || '');
  return m ? zonedTime(Number(m[1]), Number(m[2]), Number(m[3]), 0, 0, 0, zone) : NaN;
}
/** The date of a moment in the zone: "2026-10-07". */
export function dayTextOf(ms, zone = DEFAULT_ZONE) {
  const p = zonedParts(ms, zone);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}
/** The start of the day (in the zone) that holds this moment. */
export const startOfDay = (ms, zone = DEFAULT_ZONE) => dayStartOf(dayTextOf(ms, zone), zone);
/** The start of the day `n` calendar days after (or before) the day that holds this moment (a day can be 23 or 25 hours long). */
export function addDays(ms, n, zone = DEFAULT_ZONE) {
  const p = zonedParts(ms, zone);
  const shifted = new Date(Date.UTC(p.year, p.month - 1, p.day + n));
  return zonedTime(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate(), 0, 0, 0, zone);
}
/** "2026-10-07T14:30:05" (a date and time in the zone, for an input of type datetime-local). */
export function localInputValue(ms, zone = DEFAULT_ZONE) {
  const p = zonedParts(ms, zone);
  const t = (n) => String(n).padStart(2, '0');
  return `${p.year}-${t(p.month)}-${t(p.day)}T${t(p.hour)}:${t(p.minute)}:${t(p.second)}`;
}
/** The moment for what was typed in a datetime-local input ("2026-10-07T14:30" or with seconds), read in the zone; NaN when it isn't a date. */
export function fromLocalInput(text, zone = DEFAULT_ZONE) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(text || '');
  return m ? zonedTime(Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6] || 0), zone) : NaN;
}

/** The `tz` of a request (minutes WEST of UTC, like Date.getTimezoneOffset: New York is 240) as the milliseconds the clock is AHEAD of UTC (-4 hours); 0 when missing or wrong. */
export function eastMsOfTz(tz) {
  const n = Number(tz);
  return Number.isFinite(n) && tz !== '' && tz != null ? 0 - Math.max(-900, Math.min(900, Math.round(n))) * 60000 : 0;
}
